import React from 'react';

import PropTypes from 'prop-types';

import { RenderIconType } from '@adsk/alloy-react-icon';
import theme, { stylePropType } from '@adsk/alloy-react-theme';

import BasicButton from './BasicButton';
import { buttonTypePropType, StylableButtonProps } from '../consts';

const id = 'icon-button';

type IconProps = {
  size: number;
  id: string;
  'aria-hidden': boolean;
  'aria-label'?: string;
};

/**
 * Icon Button.
 */
const IconButton = React.forwardRef<
  HTMLButtonElement,
  StylableButtonProps & {
    renderIcon?: RenderIconType;
    size?: number;
  }
>(
  (
    {
      style,
      className,
      children,
      size = 24,
      renderIcon,
      'aria-label': ariaLabel,
      ...props
    },
    ref,
  ) => {
    const iconProps: IconProps = {
      size,
      id,
      'aria-hidden': false,
    };

    if (ariaLabel) {
      // overwrites the icon's default aria-label
      iconProps['aria-label'] = ariaLabel;
    }

    return (
      <BasicButton
        ref={ref}
        className={className}
        aria-labelledby={id}
        style={[
          {
            padding: 0,
            width: size,
            height: size,
            color: theme.tokens.colors.icon.secondary.value,
            '&:hover': {
              color: theme.tokens.colors.icon.hover.value,
            },
            '&:active': {
              color: theme.tokens.colors.icon.selected.value,
            },
            '&:focus': {
              color: theme.tokens.colors.icon.pressed.value,
            },
            '&[disabled]': {
              color: theme.tokens.colors.icon.inactive.value,
              cursor: 'not-allowed',
            },
          },
          style,
        ]}
        {...props}
      >
        {children || renderIcon?.(iconProps)}
      </BasicButton>
    );
  },
);

IconButton.displayName = 'IconButton';

IconButton.propTypes = {
  /** BasicButton's content */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** button html type, eg: submit */
  type: buttonTypePropType,
  /** Icon size, passed only when using `iconType` */
  size: PropTypes.number,
  /** Icon type to display on left */
  renderIcon: PropTypes.func,
};

export default IconButton;
